import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Modal, Popover } from 'antd';

import EventFormElement from '../events/EventFormElement';

/**
 * One cell of the item table, open for editing: the element's OWN input —
 * `EventFormElement`, the component the item form renders every field with —
 * so the cell formats, parses and validates exactly as the form does (a
 * date's grammar and bounds, a measurement's units, a lookup's picker, a
 * phone's country). Nothing about a value is re-implemented for the table.
 *
 * Desktop: a popover anchored on the cell. Enter saves, Esc cancels, and
 * clicking away saves — the spreadsheet's "blur commits". Enter belongs to
 * a control that is using it (an open select choosing an option, a long
 * text's new line — there ⌘/Ctrl+Enter saves), and Esc to an open dropdown
 * first.
 *
 * Phone: a small bottom sheet with Save and Cancel. Dismissing a sheet by
 * its mask is a CANCEL there, not a save: on a phone the mask is where a
 * thumb lands on the way to something else.
 *
 * Rich text, on desktop: a MODAL rather than a popover (live pass,
 * 2026-09-26 — it used to open the item). A paragraph wants room and the
 * formatting toolbar, and a stray click beside a popover would save half a
 * thought, so the modal's mask does nothing: ⌘/Ctrl+Enter or Save saves,
 * Esc or Cancel discards. It is the same `EventFormElement` — the form's
 * RichTextEditor (TipTap, markdown in and out) — and the same save.
 *
 * `errorMessage` is the reason the last attempt was refused — the client's
 * own required check, or the server's sentence — shown under the input the
 * way the form shows it.
 */
// Controls whose own Enter means something (it presses them): a plain Enter
// on one is theirs, never the cell editor's Save.
const ENTER_OWNING_CONTROLS = 'button, a[href], [role="button"], [role="menuitem"], [role="option"], [role="tab"], [role="switch"], summary';

export default function TableCellEditor({
	open,
	item,
	element,
	elementDefs,
	cardMeta,
	initialValue,
	errorMessage = null,
	isMobile = false,
	// The row's identity (a RecordTitle), heading the rich-text modal: the
	// field's own label is already on the field, so the title says WHICH
	// record the paragraph belongs to.
	recordTitle = null,
	onSave,
	onCancel,
	children,
}) {
	const [draft, setDraft] = useState(initialValue);
	// The latest value, synchronously: a picker commits typed text in its own
	// Enter handler, which runs BEFORE ours bubbles up, and a save must read
	// what it just committed rather than the render before it.
	const draftRef = useRef(initialValue);
	const closingRef = useRef(false);

	useEffect(() => {
		if (!open) return;
		setDraft(initialValue);
		draftRef.current = initialValue;
		closingRef.current = false;
	}, [open, initialValue]);

	// Focus lands in the input, so typing starts at once (the popover and the
	// sheet render their content a tick after `open`).
	const formRef = useRef(null);
	const focusField = useCallback(() => {
		const target = formRef.current?.querySelector('input:not([type="hidden"]), textarea, [contenteditable="true"], .ant-select-selector, button.ant-segmented-item, [tabindex="0"]');
		if (!target?.focus) return;
		target.focus();
		// A paragraph is continued, not started over: the caret goes to the
		// end of the rich text (ProseMirror follows the DOM selection).
		if (target.getAttribute('contenteditable') === 'true' && window.getSelection) {
			const range = document.createRange();
			range.selectNodeContents(target);
			range.collapse(false);
			const selection = window.getSelection();
			selection.removeAllRanges();
			selection.addRange(range);
		}
	}, []);
	useEffect(() => {
		if (!open) return undefined;
		const timer = setTimeout(focusField, 0);
		return () => clearTimeout(timer);
	}, [open, focusField]);

	const handleChange = useCallback((name, value) => {
		draftRef.current = value;
		setDraft(value);
	}, []);

	const save = useCallback(() => {
		if (closingRef.current) return;
		closingRef.current = true;
		onSave(draftRef.current);
	}, [onSave]);

	const cancel = useCallback(() => {
		if (closingRef.current) return;
		closingRef.current = true;
		onCancel();
	}, [onCancel]);

	const handleKeyDown = (event) => {
		// The list's own keys (↑↓ move, Enter open, Esc close the pane) must
		// not fire from inside an editor.
		event.stopPropagation();
		const target = event.target instanceof Element ? event.target : null;
		const busy = target?.closest('.ant-select-open, .ant-picker-dropdown, .ant-select-dropdown');
		if (event.key === 'Escape') {
			if (busy) return;
			event.preventDefault();
			cancel();
			return;
		}
		if (event.key === 'Enter' && !event.nativeEvent?.isComposing) {
			if (busy) return;
			const isTextArea = target?.tagName === 'TEXTAREA' || target?.closest('[contenteditable="true"]');
			if (isTextArea && !(event.metaKey || event.ctrlKey)) return;
			// A control that answers Enter itself — the rich-text toolbar's
			// Bold, its markdown switch, a link, a menu row — keeps it: Enter
			// presses the button (the review of the live-pass merges: it saved
			// the cell instead, and preventDefault swallowed the press).
			// ⌘/Ctrl+Enter is still the modal's Save from anywhere.
			if (target?.closest(ENTER_OWNING_CONTROLS) && !(event.metaKey || event.ctrlKey)) return;
			event.preventDefault();
			save();
		}
	};

	// ⌘/Ctrl+Enter must SAVE before the rich-text editor sees it: TipTap
	// binds Mod-Enter to a hard break, and ProseMirror's own listener on the
	// editable runs before a bubbling handler — so the saved markdown gained
	// a trailing line break. Caught on the way down instead.
	const handleKeyDownCapture = (event) => {
		if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey) || event.nativeEvent?.isComposing) return;
		const target = event.target instanceof Element ? event.target : null;
		if (!target?.closest('[contenteditable="true"]')) return;
		event.preventDefault();
		event.stopPropagation();
		save();
	};

	const editingItem = { ...item, elements: { ...(item?.elements || {}), [element.name]: draft } };
	const isRichText = element.dataType === 'rich-text';
	const hint = (element.dataType === 'text-large' || isRichText) ? '⌘↵ save · Esc cancel' : '↵ save · Esc cancel';
	const field = (
		<EventFormElement
			event={editingItem}
			element={element}
			value={draft}
			onChange={handleChange}
			cardMeta={cardMeta}
			showIconBadge={false}
			errorMessage={errorMessage}
			siblingDefs={elementDefs}
		/>
	);
	const form = (
		<div ref={formRef} className="card-table__editor" onKeyDownCapture={handleKeyDownCapture} onKeyDown={handleKeyDown} role="presentation" data-testid="cell-editor">
			{field}
			<div className="card-table__editor-actions">
				<span className="card-table__editor-hint">{hint}</span>
				<Button size="small" onClick={cancel}>Cancel</Button>
				<Button size="small" type="primary" onClick={save}>Save</Button>
			</div>
		</div>
	);

	if (isRichText && !isMobile) {
		return (
			<EditorGuard>
				{children}
				<Modal
					open={open}
					title={recordTitle || element?.label?.singular || element?.name}
					width={720}
					className="card-table__modal"
					// Only Save saves: a click beside the editor is not "done".
					maskClosable={false}
					onCancel={cancel}
					// The modal takes focus for itself as it opens; the field
					// gets it back once the modal has settled.
					afterOpenChange={visible => { if (visible) focusField(); }}
					destroyOnHidden
					footer={[
						<span key="hint" className="card-table__editor-hint">{hint}</span>,
						<Button key="cancel" onClick={cancel}>Cancel</Button>,
						<Button key="save" type="primary" onClick={save}>Save</Button>,
					]}
				>
					<div ref={formRef} className="card-table__editor card-table__editor--rich" onKeyDownCapture={handleKeyDownCapture} onKeyDown={handleKeyDown} role="presentation" data-testid="cell-editor">
						{field}
					</div>
				</Modal>
			</EditorGuard>
		);
	}

	if (isMobile) {
		return (
			<EditorGuard>
				{children}
				<Drawer
					open={open}
					placement="bottom"
					height="auto"
					title={element?.label?.singular || element?.name}
					onClose={cancel}
					destroyOnClose
					className="card-table__sheet"
				>
					{form}
				</Drawer>
			</EditorGuard>
		);
	}

	return (
		<EditorGuard>
			<Popover
				open={open}
				content={form}
				trigger="click"
				placement="bottomLeft"
				destroyTooltipOnHide
				overlayClassName="card-table__popover"
				// Clicking away commits, like leaving a spreadsheet cell.
				onOpenChange={(next) => { if (!next && open) save(); }}
			>
				{children}
			</Popover>
		</EditorGuard>
	);
}

const stop = event => event.stopPropagation();

/**
 * The editor's popover and sheet render in a PORTAL, but React bubbles a
 * portal's events up the COMPONENT tree — through the cell and into the
 * row's onRow onClick. Without this, clicking into the input, a picker's
 * option or Save also SELECTED the row: the detail pane opened on desktop,
 * and on a phone the row's inline detail unfolded under the open sheet
 * (card-table review W1). The guard wraps the whole editor (sheet header,
 * mask and popover padding included, not just the form) and stops the
 * events a row or the list listens for.
 *
 * mousedown / pointerdown are deliberately NOT stopped: antd's pickers and
 * selects close their dropdowns on an outside mousedown, and the popover's
 * own click-away is a capture-phase window listener either way.
 * `display: contents` keeps the wrapper out of the cell's layout.
 */
function EditorGuard({ children }) {
	return (
		<span
			className="card-table__editor-guard"
			onClick={stop}
			onDoubleClick={stop}
			onKeyDown={stop}
			role="presentation"
		>
			{children}
		</span>
	);
}
